import React from 'react';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiHeart, FiStar, FiUsers, FiMessageSquare } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import settings from '../config/settings';
import SEO from '../components/SEO';

const Contact = () => {
  return (
    <div className="animate-fadeIn">
      <SEO page="contact" />
      {/* Hero Section with Background */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-teal-800 to-cyan-900 pt-32 pb-40 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-20 h-20 border-4 border-white"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 border-4 border-white rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center space-x-2 text-sm text-emerald-200 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <span className="text-emerald-400">/</span>
            <span className="text-white font-medium">Contact</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-aos="fade-up">
              <SafeIcon icon={FiSend} className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Contact Us</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-aos="fade-up" data-aos-delay="100">
              Get in Touch
            </h1>
            
            <p className="text-xl text-emerald-100 leading-relaxed" data-aos="fade-up" data-aos-delay="200">
              Ready to give your child the best start in life? We're here to answer all your questions and 
              help you discover why families choose us.
            </p>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <a href={`tel:${settings.phones[0].number}`} className="text-center group cursor-pointer" data-aos="fade-up">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors duration-300">
                <SafeIcon icon={FiPhone} className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors duration-300">Call Us</div>
            </a>
            <a href={settings.whatsapp.link} target="_blank" rel="noopener noreferrer" className="text-center group cursor-pointer" data-aos="fade-up" data-aos-delay="100">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-300">
                <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-green-600" />
              </div>
              <div className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors duration-300">WhatsApp</div>
            </a>
            <a href={`mailto:${settings.email}`} className="text-center group cursor-pointer" data-aos="fade-up" data-aos-delay="200">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-blue-600" />
              </div>
              <div className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">Email Us</div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Info Side */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Visit Our Campus</h2>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed break-words">
                  Located in the heart of Kukatpally, our modern facility is designed with your child's safety and learning in mind. Come experience our nurturing environment firsthand.
                </p>
              </div>

              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-aos="fade-up">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Our Location</h3>
                      <p className="text-gray-600 leading-relaxed mb-3">
                        {settings.address.full}
                      </p>
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(settings.address.full)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors"
                      >
                        Get Directions →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-aos="fade-up" data-aos-delay="100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 flex-shrink-0">
                      <SafeIcon icon={FiPhone} className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Phone Support</h3>
                      {settings.phones.map((phone, index) => (
                        <div key={index} className="mb-2">
                          <p className="text-gray-600 font-medium">
                            {index === 0 ? 'Main Line: ' : 'Admin: '}
                            <a href={`tel:${phone.number}`} className="text-blue-600 hover:text-blue-700 transition-colors">
                              {phone.display}
                            </a>
                          </p>
                        </div>
                      ))}
                      <p className="text-sm text-gray-500 mt-2">Available Mon-Fri, 8:30 AM - 3:00 PM</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-aos="fade-up" data-aos-delay="200">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-600 flex-shrink-0">
                      <SafeIcon icon={FiMail} className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                      <div className="mb-3">
                        <a
                          href={`mailto:${settings.email}`}
                          className="text-orange-600 hover:text-orange-700 transition-colors font-medium break-all text-sm md:text-base"
                        >
                          {settings.email}
                        </a>
                      </div>
                      <p className="text-sm text-gray-500">We respond within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-aos="fade-up" data-aos-delay="300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 flex-shrink-0">
                      <SafeIcon icon={FiClock} className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Operating Hours</h3>
                      <p className="text-gray-600 font-medium mb-1">{settings.hours.weekdays}</p>
                      <p className="text-gray-500 text-sm">{settings.hours.note}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div id="contact-form" className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100" data-aos="fade-up">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Schedule Your Visit</h2>
                <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours to arrange a personalized tour.</p>
              </div>

              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span>Parent's Name</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span>Phone Number</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span>Email Address</span>
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="Enter your email"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Child's Age</label>
                    <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all">
                      <option>Select Age Group</option>
                      <option>2-3 Years</option>
                      <option>3-4 Years</option>
                      <option>4-5 Years</option>
                      <option>5-6 Years</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Preferred Visit Date</label>
                    <input
                      type="date"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Tell us about your child</label>
                  <textarea
                    rows="4"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="Any specific questions, concerns, or information about your child's needs?"
                  ></textarea>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    className="mt-1 w-4 h-4 text-emerald-600 bg-gray-50 border-gray-200 rounded focus:ring-emerald-500"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-600">
                    I'd like to receive updates about school activities and events
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Submit Inquiry
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy. We'll never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Find Us on the Map</h2>
            <p className="text-lg text-gray-600">Located in the heart of Kukatpally, easily accessible by road and metro.</p>
          </div>

          <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg" data-aos="fade-up">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.357!2d78.3975!3d17.4933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb91b9b9b9b9b9%3A0x1234567890abcdef!2sTHE%20ASIAN%20INTERNATIONAL%20SCHOOL!5e0!3m2!1sen!2sin!4v1703123456789!5m2!1sen!2sin"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="THE ASIAN INTERNATIONAL SCHOOL Location"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4 text-sm md:text-base">Need help finding us?</p>
            <div className="flex flex-row gap-3 sm:gap-4 justify-center max-w-sm sm:max-w-none mx-auto">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(settings.address.full)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2 flex-1 sm:flex-initial"
              >
                <SafeIcon icon={FiMapPin} className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Get Directions</span>
              </a>
              <a
                href={`tel:${settings.phones[0].number}`}
                className="bg-green-600 text-white px-4 sm:px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2 flex-1 sm:flex-initial"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Call Now</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;